/* 
-----------------------------------------------------------------------------
| SHIFT SELECTION
-----------------------------------------------------------------------------
- description: its only purpose is to manage the value of booking.shiftId
  and reset other values or navigate to other route if needed
*/
import { FC, useEffect, useMemo, useRef } from "react";
import { useApp } from "../context/useApp";
import { Selection, SelectionItem } from "../parts/inputs/Selection";
import { _ } from "../utils/helpers";
import { cache, DEFAULT_DIALOG_HANDLE, rumba, metrics, translator } from "../utils/symbols"; //eslint-disable-line -- used in comented code
import { Icon } from "../parts/Icon";
import { IconName } from "../types";

export const ShiftSelectionWithTables: FC<{label?: string}> = ({label}) => {

    const {
        state: { booking, shifts, dialogHandles },
        actions: { updateShifts, genericError }
    } = useApp()

    const lastDateRequested = useRef('')
    const discounts = useRef(false)

    const layoutDialog = dialogHandles.get('table-map-dialog') ?? DEFAULT_DIALOG_HANDLE

    const {selectionItems, specialEvent}: {selectionItems: SelectionItem[], specialEvent: string | null} = useMemo(() => {
        let specialEvent = ""
        const selectionItems = shifts.map(shift => {
            specialEvent = specialEvent || (shift.specialEvent??"")
            const icon: IconName | undefined = shift.specialEvent 
                ? "special-event-small" 
                : shift.specialOffer 
                ? "discount"
                : undefined
            return {id: shift.id, value: shift.time, disabled: !shift.available, icon}
        })
        return {selectionItems, specialEvent}
        //eslint-disable-next-line
    }, [shifts, booking.date])

    const getShifts = async ():Promise<void> => {

        const date = booking.date.split('T')[0]
        if (lastDateRequested.current === date) return
        lastDateRequested.current = date

        const shiftsInCache = cache.get('shifts', date)
        if (shiftsInCache) {
            updateShifts(shiftsInCache)
            return
        }

        const response = await rumba.getShifts(booking.date)
        if (response.result !== 'ok') {
            genericError(getShifts)
            return
        }
        /* shifts may differ with the last ones obtained due to
         * the possibility to close them. So if shift selected and
         * shift selected not in new shifts selected, reset shift. */
        if (response.shifts.findIndex((shift) => shift.id === booking.shiftId) === -1) 
            booking.unselectShift().render()

        updateShifts(response.shifts)
        cache.add('shifts', date, response.shifts)
    }

    const handleShiftSelection = async (selectedItem: SelectionItem, goToNextStep: boolean = true) => {
        layoutDialog.closeDialog()
        /* SELECT SHIFT ID
        * shift id updated, so next step must be tickets-selection */
       
        const selectedShift = shifts.find(shift => shift.id === selectedItem.id)
        if (selectedShift === undefined) return
        if (selectedShift.id !== booking.shiftId) {
            booking.selectShift(selectedShift)
            await metrics.shiftSelectionEvent(booking.getBookingDataByReference())
        }
        if (goToNextStep) booking.setBookingStep('tickets')
        // change: show always
        // const { shiftShown, ...rest } = load('localStorage', 'booking-instructions', DEFAULT_BOOKING_INSTRUCTIONS)
        // save('localStorage', 'booking-instructions', {...rest, shiftShown: true})
        booking.render()
    }

    useEffect(() => {
        updateShifts([])
        getShifts() 
    }, [])//eslint-disable-line

    return (
        <Selection
            id="shift-selection" 
            FieldsetLabel={label}
            className="mt-7"
            value={booking.shiftId}
            onSelection={handleShiftSelection}
            selectionItems={selectionItems}
        >
            {discounts.current && (
                //show discounts icon description
                <p className="text softer s16-400 df">
                    <Icon 
                        icon="star" 
                        className="jcfs" 
                        aria-hidden="true"
                    />
                        <span aria-hidden="true">
                            {_('2sh_speci__')}
                        </span>
                </p>
            )}
            {specialEvent !== '' && (
                //show special event text
                <p className="text s16-600 df aic">
                    <Icon
                        icon="special-event" 
                        className="jcfs" 
                        aria-hidden="true"
                    />
                    <span aria-hidden="true">
                        {translator.extractTranslationFromString(specialEvent)}
                    </span>
                </p>
            )}
        </Selection>
    )
} 

